import { api } from '../../shared/api/client'

/**
 * Справочники категорий и характеристик маркетплейсов.
 *
 * ЧТЕНИЕ идёт из БД PIM и кабинета не требует: справочник у площадки один на
 * всех продавцов. ЗАГРУЗКА с площадки идёт под токеном кабинета, поэтому
 * маршруты синхронизации принимают sellerId.
 */

// ── Wildberries ─────────────────────────────────────────────────────

export interface WbSubject {
  subject_id: number
  subject_name: string
  parent_id: number | null
  parent_name: string | null
  /** Когда для предмета последний раз грузили характеристики; null — ни разу. */
  characteristics_synced_at: string | null
  is_active: boolean | number
}

export interface WbSubjectCharacteristic {
  id: number
  subject_id: number
  charc_id: number
  name: string
  is_required: boolean | number
  value_type: 'string' | 'number'
  unit_name: string | null
  is_list: boolean | number
  /** Максимум значений по справочнику WB; 0/null означает без явного предела. */
  max_count: number | null
  dictionary_code: string | null
  is_active: boolean | number
  dictionary_values: number
}

// ── OZON ────────────────────────────────────────────────────────────

export interface OzonCategoryOption {
  description_category_id: number
  category_name: string
  /** Сколько типов у категории. Ноль означает, что пару из неё не составить. */
  type_count: number
}

export interface OzonTypeOption {
  type_id: number
  type_name: string
  /** Сколько активных атрибутов пары уже лежит в справочнике PIM. */
  attributes_loaded: number
}

export interface OzonCategoryAttribute {
  attribute_id: number | string
  name: string
  description: string | null
  type: string
  is_required: boolean | number
  is_collection: boolean | number
  /** Максимум элементов коллекции OZON; 0/null означает без явного предела. */
  max_value_count: number | null
  group_name: string | null
  dictionary_id: string | null
  is_active: boolean | number
}

// ── Яндекс Маркет ───────────────────────────────────────────────────

/**
 * Категория Маркета из справочника ПИМ (`ym_categories`, дерево).
 *
 * В отличие от WB/OZON у Маркета нет пары «категория + тип» — товары и
 * характеристики привязываются к ОДНОЙ листовой категории, поэтому у неё есть
 * `is_leaf` и `path` (полный путь для отображения без рекурсии на клиенте).
 */
export interface YmCategoryOption {
  id: number
  parent_id: number | null
  name: string
  path: string | null
  is_leaf: boolean | number
  synced_at: string | null
}

/** Единица измерения характеристики: `unit.defaultUnitId` → имя из `unit.units`. */
export interface YmParameterUnit {
  defaultUnitId?: number | string | null
  units?: Array<{ id: number | string; name: string }>
}

export interface YmCategoryParameter {
  category_id: number
  parameter_id: number
  name: string
  /** ENUM | NUMERIC | TEXT | BOOLEAN — форма ответа площадки не документирована жёстко. */
  type: string
  unit: YmParameterUnit | null
  required: boolean | number
  filtering: boolean | number
  /** Различающая характеристика склейки (SPEC этап 3: «склейка = тема внутри носителя»). */
  distinctive: boolean | number
  multivalue: boolean | number
  allowCustomValues: boolean | number
  constraints: unknown
  recommendationTypes: unknown[] | null
  description: string | null
  is_active: boolean | number
  synced_at: string | null
  /** Значения ENUM до `values_limit` (по умолчанию 200) — см. values_truncated. */
  values: Array<{ value_id: number; value: string; description: string | null }>
  values_truncated?: boolean
}

export interface YmParameterValueOption {
  value_id: number
  value: string
  description: string | null
}

export interface YmReferenceSyncResult {
  jobId: string
  [key: string]: unknown
}

/** Нормализованное значение локального словаря площадки. */
export interface MarketplaceDictionaryValue {
  /** Внутренний код значения площадки, если она его предоставляет. */
  value_id: number | string | null
  /** Текст, который сохраняется в характеристике и уходит в карточку. */
  value: string
}

/** Результат догрузки характеристик — показывается человеку после кнопки. */
export interface WbCharacteristicsSyncResult {
  jobId: string
  subjects?: number
  characteristics?: number
  deactivated?: number
  [key: string]: unknown
}

export interface OzonAttributesSyncResult {
  jobId: string
  pairs: number
  attributes: number
  deactivated: number
  values: number
  failedPairs: Array<{ descriptionCategoryId: number; typeId: number; error: string }>
}

function unwrap<T>(response: any): T {
  return (response?.data?.data ?? response?.data) as T
}

function dictionaryValues(payload: any): MarketplaceDictionaryValue[] {
  const list = Array.isArray(payload) ? payload : (payload?.values ?? payload?.items ?? [])
  return (Array.isArray(list) ? list : [])
    .map((item: any) =>
      typeof item === 'string' || typeof item === 'number'
        ? { value_id: null, value: String(item).trim() }
        : {
            value_id: item?.value_id ?? item?.id ?? item?.code ?? null,
            value: String(item?.value ?? item?.name ?? item?.label ?? '').trim(),
          },
    )
    .filter((item: MarketplaceDictionaryValue) => item.value)
}

export const marketplaceReferenceAPI = {
  /** Кабинеты площадки: нужны только как источник токена для загрузки. */
  getSellers: async (marketplace: 'wildberries' | 'ozon' | 'yandex_market') => {
    const payload = unwrap<any>(await api.get('/sellers', { params: { marketplace } }))
    const list = Array.isArray(payload) ? payload : (payload?.sellers ?? payload?.data ?? [])
    return (list as any[]).map((seller) => ({
      seller_id: Number(seller.seller_id ?? seller.id),
      seller_name: String(seller.seller_name ?? seller.name ?? `Кабинет ${seller.seller_id}`),
    }))
  },

  getWbSubjects: async (search?: string): Promise<WbSubject[]> => {
    const payload = unwrap<{ subjects: WbSubject[] }>(
      await api.get('/wildberries/reference/subjects', {
        params: search ? { search } : undefined,
        timeout: 60000,
      }),
    )
    return payload?.subjects ?? []
  },

  getWbSubjectCharacteristics: async (subjectId: number): Promise<WbSubjectCharacteristic[]> => {
    const payload = unwrap<{ characteristics: WbSubjectCharacteristic[] }>(
      await api.get(`/wildberries/reference/subjects/${subjectId}/characteristics`),
    )
    return payload?.characteristics ?? []
  },

  /** Значения уже загруженного в PIM словаря WB; наружу к площадке не ходит. */
  getWbDictionaryValues: async (
    dictionaryCode: string,
    search?: string,
  ): Promise<MarketplaceDictionaryValue[]> => {
    const payload = unwrap<any>(
      await api.get(
        `/wildberries/reference/dictionaries/${encodeURIComponent(dictionaryCode)}/values`,
        { params: search ? { search } : undefined },
      ),
    )
    return dictionaryValues(payload)
  },

  /**
   * Догрузка характеристик ОДНОГО предмета WB.
   *
   * Смысл именно в точечной загрузке: полный справочник — тысячи предметов,
   * то есть тысячи запросов к площадке, а карточки публикуются в единицы
   * предметов. jobId не передаём — его создаёт бэкенд (конвенция проекта),
   * прогресс приходит по WebSocket.
   */
  syncWbSubjectCharacteristics: async (
    sellerId: number,
    subjectId: number,
    options: { withDictionaries?: boolean } = {},
  ): Promise<WbCharacteristicsSyncResult> =>
    unwrap<WbCharacteristicsSyncResult>(
      await api.post(`/wildberries/sellers/${sellerId}/reference/characteristics/sync`, {
        subjectIds: [subjectId],
        scope: 'linked',
        withDictionaries: Boolean(options.withDictionaries),
      }),
    ),

  getOzonCategories: async (search?: string): Promise<OzonCategoryOption[]> => {
    const payload = unwrap<{ categories: OzonCategoryOption[] }>(
      await api.get('/ozon/reference/categories', {
        params: search ? { search } : undefined,
        timeout: 60000,
      }),
    )
    return payload?.categories ?? []
  },

  getOzonCategoryTypes: async (
    descriptionCategoryId: number,
    search?: string,
  ): Promise<OzonTypeOption[]> => {
    const payload = unwrap<{ types: OzonTypeOption[] }>(
      await api.get(`/ozon/reference/categories/${descriptionCategoryId}/types`, {
        params: search ? { search } : undefined,
      }),
    )
    return payload?.types ?? []
  },

  getOzonCategoryAttributes: async (
    descriptionCategoryId: number,
    typeId: number,
  ): Promise<OzonCategoryAttribute[]> => {
    const payload = unwrap<{ attributes: OzonCategoryAttribute[] }>(
      await api.get('/ozon/reference/attributes', {
        params: { descriptionCategoryId, typeId },
      }),
    )
    return payload?.attributes ?? []
  },

  /** Значения словаря атрибута конкретной пары OZON из локального справочника PIM. */
  getOzonAttributeValues: async (
    descriptionCategoryId: number,
    typeId: number,
    attributeId: number | string,
    search?: string,
  ): Promise<MarketplaceDictionaryValue[]> => {
    const payload = unwrap<any>(
      await api.get('/ozon/reference/attribute-values', {
        params: {
          descriptionCategoryId,
          typeId,
          attributeId,
          ...(search ? { search } : {}),
        },
      }),
    )
    return dictionaryValues(payload)
  },

  /** Догрузка характеристик ОДНОЙ пары «категория + тип» OZON. */
  syncOzonCategoryAttributes: async (
    sellerId: number,
    pair: { descriptionCategoryId: number; typeId: number },
    options: { withDictionaries?: boolean } = {},
  ): Promise<OzonAttributesSyncResult> =>
    unwrap<OzonAttributesSyncResult>(
      await api.post(`/ozon/sellers/${sellerId}/reference/attributes/sync`, {
        pairs: [pair],
        withDictionaries: Boolean(options.withDictionaries),
      }),
    ),

  // ── Яндекс Маркет ─────────────────────────────────────────────────

  /**
   * Категории Маркета из справочника ПИМ. Общий для площадки — сортировка по
   * `path`, чтобы совпадающие названия из разных веток дерева не перемешивались.
   */
  getYmCategories: async (
    filter: { search?: string; parentId?: number; leafOnly?: boolean; limit?: number } = {},
  ): Promise<YmCategoryOption[]> => {
    const payload = unwrap<{ categories: YmCategoryOption[] }>(
      await api.get('/yandex-market/reference/categories', {
        params: {
          search: filter.search || undefined,
          parent_id: filter.parentId,
          leaf_only: filter.leafOnly ? '1' : undefined,
          limit: filter.limit,
        },
        timeout: 60000,
      }),
    )
    return payload?.categories ?? []
  },

  getYmCategoryParameters: async (categoryId: number): Promise<YmCategoryParameter[]> => {
    const payload = unwrap<{ parameters: YmCategoryParameter[] }>(
      await api.get(`/yandex-market/reference/categories/${categoryId}/parameters`),
    )
    return payload?.parameters ?? []
  },

  getYmParameterValues: async (
    categoryId: number,
    parameterId: number,
    search?: string,
  ): Promise<YmParameterValueOption[]> => {
    const payload = unwrap<{ values: YmParameterValueOption[] }>(
      await api.get('/yandex-market/reference/parameter-values', {
        params: { category_id: categoryId, parameter_id: parameterId, search: search || undefined },
      }),
    )
    return payload?.values ?? []
  },

  /**
   * Полная синхронизация дерева категорий Маркета — справочник общий для
   * площадки, поэтому запрос без параметров (в отличие от точечной догрузки
   * характеристик WB/OZON по одной категории).
   */
  syncYmCategoriesTree: async (): Promise<YmReferenceSyncResult> =>
    unwrap<YmReferenceSyncResult>(await api.post('/yandex-market/reference/categories/sync', {})),

  /** Догрузка характеристик набора категорий Маркета. */
  syncYmCategoryParameters: async (categoryIds: number[]): Promise<YmReferenceSyncResult> =>
    unwrap<YmReferenceSyncResult>(
      await api.post('/yandex-market/reference/parameters/sync', { category_ids: categoryIds }),
    ),
}

/**
 * Слияние ответа поиска с уже загруженным списком.
 *
 * Заменять список целиком нельзя: выбранное значение выпало бы из него, и поле
 * показало бы голый код вместо названия. Поиск ДОПОЛНЯЕТ справочник в памяти,
 * а не подменяет его.
 */
export function mergeReference<T>(
  current: T[],
  incoming: T[],
  key: (item: T) => number | string,
): T[] {
  const byKey = new Map<number | string, T>()
  for (const item of current) byKey.set(key(item), item)
  for (const item of incoming) byKey.set(key(item), item)
  return [...byKey.values()]
}

/** Ссылка на категорию площадки в схеме сопоставления (scope_ref). */
export function wbScopeRef(subjectId: number | null | undefined): string {
  return subjectId ? String(subjectId) : ''
}

/**
 * Пара OZON в виде «категория:тип» — ровно так её хранит
 * `mp_characteristic_mappings.scope_ref`.
 */
export function ozonScopeRef(
  descriptionCategoryId: number | null | undefined,
  typeId: number | null | undefined,
): string {
  if (!descriptionCategoryId || !typeId) return ''
  return `${descriptionCategoryId}:${typeId}`
}
